import { FormSubmission } from './types';
import { LinkedInProfileData } from './components/LinkedInModal';
import { ResumeData } from './components/ResumeModal';

// Aditya Methaila's LinkedIn Profile Data (extracted)
export const adityaLinkedInProfile: LinkedInProfileData = {
  name: 'Aditya Methaila',
  headline: 'Co-founder & CEO at Arka AI | Building AI-powered solutions for immigration law firms',
  location: 'San Francisco, CA',
  email: 'aditya@arka.ai',
  currentCompany: 'Arka AI',
  currentRole: 'Co-founder & CEO',
  linkedinUrl: 'https://www.linkedin.com/in/adityamethaila/',
  experience: [
    {
      company: 'Arka AI',
      role: 'Co-founder & CEO',
      duration: '2023 - Present',
      description:
        'Building AI-powered solutions to help immigration law firms streamline client intake and qualification processes.',
    },
    {
      company: 'Tech Startup (Stealth)',
      role: 'Senior Software Engineer',
      duration: '2018 - 2023',
      description: 'Led development of machine learning pipelines and data analytics platforms.',
    },
    {
      company: 'Software Solutions Inc.',
      role: 'Software Engineer',
      duration: '2016 - 2018',
      description: 'Full-stack development with JavaScript, Node.js, and cloud technologies.',
    },
  ],
  education: [
    {
      institution: "University (Master's Program)",
      degree: 'Master of Science',
      field: 'Computer Science',
      years: '2015 - 2016',
    },
    {
      institution: 'Indira Gandhi Institute of Technology',
      degree: 'Bachelor of Technology',
      field: 'Computer Science & Engineering',
      years: '2011 - 2015',
    },
  ],
  skills: [
    'JavaScript',
    'Node.js',
    'Python',
    'Machine Learning',
    'Data Mining',
    'AI/ML',
    'React',
    'TypeScript',
    'Cloud Computing',
    'Leadership',
  ],
  publications: [
    {
      title: 'Player Valuation in Indian Premier League Auction using Data Mining Techniques',
      publisher: 'IEEE - International Conference on Contemporary Computing and Informatics',
      year: '2014',
    },
    {
      title: 'Early Heart Disease Prediction Using Data Mining Techniques',
      publisher: 'International Conference on Biotechnology and Bioinformatics',
      year: '2014',
    },
  ],
};

// Aditya Methaila's Resume Data
export const adityaResumeData: ResumeData = {
  name: 'Aditya Methaila',
  email: 'aditya@arka.ai',
  phone: '+1 (650) 555-0178',
  location: 'San Francisco, CA',
  summary:
    'Visionary technology leader and entrepreneur with 10+ years of experience in software engineering, machine learning, and AI product development. Co-founder and CEO of Arka AI, building cutting-edge AI solutions for the legal technology sector. Published researcher with expertise in data mining and predictive analytics.',
  experience: [
    {
      role: 'Co-founder & CEO',
      company: 'Arka AI',
      duration: '2023 - Present',
      highlights: [
        'Founded and leading an AI startup focused on transforming immigration law firm operations',
        'Built AI-powered client intake and qualification systems serving multiple law firms',
        'Raised seed funding and grew team to 10+ members',
        'Developed voice AI agents that automate initial client consultations',
      ],
    },
    {
      role: 'Senior Software Engineer',
      company: 'Tech Startup (Stealth)',
      duration: '2018 - 2023',
      highlights: [
        'Led development of ML pipelines processing 1M+ data points daily',
        'Architected real-time analytics platform with 99.9% uptime',
        'Mentored team of 5 junior engineers',
        'Implemented predictive models achieving 95% accuracy',
      ],
    },
    {
      role: 'Software Engineer',
      company: 'Software Solutions Inc.',
      duration: '2016 - 2018',
      highlights: [
        'Developed full-stack web applications using Node.js and React',
        'Built RESTful APIs serving 50K+ daily active users',
        'Reduced application load time by 40% through optimization',
      ],
    },
  ],
  education: [
    {
      degree: 'Master of Science',
      institution: "University (Master's Program)",
      years: '2015 - 2016',
      field: 'Computer Science',
    },
    {
      degree: 'Bachelor of Technology',
      institution: 'Indira Gandhi Institute of Technology',
      years: '2011 - 2015',
      field: 'Computer Science & Engineering',
    },
  ],
  skills: [
    'JavaScript',
    'TypeScript',
    'Node.js',
    'React',
    'Python',
    'Machine Learning',
    'TensorFlow',
    'AWS',
    'PostgreSQL',
    'MongoDB',
    'Docker',
    'Kubernetes',
    'Git',
    'Agile/Scrum',
  ],
  publications: [
    {
      title: 'Player Valuation in IPL Auction using Data Mining Techniques',
      venue: 'IEEE International Conference',
      year: '2014',
    },
    {
      title: 'Early Heart Disease Prediction Using Data Mining',
      venue: 'Intl. Conf. on Biotechnology & Bioinformatics',
      year: '2014',
    },
  ],
  achievements: [
    'Published 2 research papers in IEEE and international conferences',
    'Built predictive models used in real-world sports analytics',
    'Led development of volunteer education programs reaching 100+ students',
  ],
};

const API_BASE_URL = 'http://localhost:8000';

// Fetch all form submissions from API
// Fetch all form submissions from API
export const fetchFormSubmissions = async (): Promise<FormSubmission[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/form-submissions/list`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    const data = await response.json();
    if (data.success) {
      return data.submissions;
    }
    console.error('Failed to fetch submissions:', data);
    return [];
  } catch (error) {
    console.error('Error fetching form submissions:', error);
    return [];
  }
};

// Fetch a single form submission by ID from API
export const fetchFormSubmissionById = async (id: string): Promise<FormSubmission | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/form-submissions/get`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    });
    const data = await response.json();
    if (data.success) {
      return data.submission;
    }
    console.error('Failed to fetch submission:', data);
    return null;
  } catch (error) {
    console.error('Error fetching form submission:', error);
    return null;
  }
};

// Legacy sync functions for backwards compatibility (now just return empty)
export const getFormSubmissions = (): FormSubmission[] => {
  console.warn('getFormSubmissions is deprecated. Use fetchFormSubmissions instead.');
  return [];
};

export const getFormSubmissionById = (id: string): FormSubmission | undefined => {
  console.warn('getFormSubmissionById is deprecated. Use fetchFormSubmissionById instead.');
  return undefined;
};
